import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AnalyticsMode } from 'src/graphql/types/api.types';

export enum MyPrivacyState {
  View,
  Edit,
}

export interface MyPrivacySettings {
  allowIdentifiedAnalytics: boolean;
  shareDetailsWithProviderOptIn: boolean;
  marketingOptIn: boolean;
  allowPersonalizedCoaching: boolean;
}

@Component({
  selector: 'mpp-my-privacy',
  templateUrl: './my-privacy.component.html',
  styleUrls: ['./my-privacy.component.scss'],
  standalone: false,
})
export class MyPrivacyComponent implements OnInit, OnDestroy {
  downloadMyDataStateValue = MyPrivacyState.View;
  allowPersonalizedStateValue = MyPrivacyState.View;
  myPrivacyStateValue = MyPrivacyState.View;
  shareInformationStateValue = MyPrivacyState.View;
  myPrivacyState = MyPrivacyState;
  receiveAdditionalInfoState = MyPrivacyState.View;
  hipaaAuthorizationState = MyPrivacyState.View;

  @Input() privacySettings: MyPrivacySettings = {
    allowIdentifiedAnalytics: false,
    shareDetailsWithProviderOptIn: false,
    marketingOptIn: false,
    allowPersonalizedCoaching: false,
  };
  @Input() showSmartCoachingOptOut: boolean = false;
  @Input() email: string;
  @Input() marketingConsentDefault: boolean;
  @Input() allowedAnalyticsModes: Array<AnalyticsMode>;
  @Input() optInAnalyticsMode: AnalyticsMode;
  @Input() optOutAnalyticsMode: AnalyticsMode;
  @Input() hipaaConsented: boolean = false;
  @Input() hipaaConsentUrl: string;
  @Input() showHipaaAuthorization: boolean = false;
  public preloadedHipaaHtml: string;
  private unsubscribe$ = new Subject<void>();
  // boolean to determine whether or not the My Account page's 'My Privacy' section should include an 'Analytics' section
  allowAnalytics: boolean = false;
  // boolean to determine whether or not the My Account page's 'My Privacy' section should include an 'Additional Information' section
  allowAdditionalInformation: boolean = false;
  constructor(private http: HttpClient) {}

  async ngOnInit(): Promise<void> {
    // If Web eventually supports pre-therapy users, add a !preTherapy check here and in HTML for "mpp-share-information-view"

    // Show analytics if allowedAnalyticsModes has more than 1 option
    if (this.allowedAnalyticsModes?.length > 1) {
      this.allowAnalytics = true;
    }

    // Show if marketingConsentDefault is explicitly true or false (not null/undefined)
    if (this.marketingConsentDefault === true || this.marketingConsentDefault === false) {
      this.allowAdditionalInformation = true;
    }

    // Preload HIPAA consent content so it's ready when user clicks "Change"
    if (this.showHipaaAuthorization && this.hipaaConsentUrl) {
      this.preloadHipaaContent(this.hipaaConsentUrl);
    }
  }

  private preloadHipaaContent(url: string): void {
    // Signal the web platform to the backend so it returns JSON { consentUrl } instead of a cross-origin 302 redirect (the mobile path), which the browser can't follow due to CORS.
    // Sent as a query param rather than an RMDHandsetPlatform header to avoid a CORS preflight.
    let backendUrl: URL;
    try {
      backendUrl = new URL(url);
      backendUrl.searchParams.set('platform', 'Web');
    } catch (error) {
      console.error('[MyPrivacy] Failed to preload HIPAA content:', error);
      return;
    }

    this.http
      .get<{ consentUrl: string }>(backendUrl.toString())
      .pipe(
        switchMap((response) => {
          if (!response.consentUrl) {
            throw new Error('No consentUrl in response');
          }
          return this.http.get(response.consentUrl, { responseType: 'text' });
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (html) => {
          this.preloadedHipaaHtml = html;
        },
        (error) => {
          console.error('[MyPrivacy] Failed to preload HIPAA content:', error);
        },
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onAnalyticsChange(state: boolean): void {
    this.privacySettings.allowIdentifiedAnalytics = state;
  }

  onShareInformationChange(state: boolean): void {
    this.privacySettings.shareDetailsWithProviderOptIn = state;
  }

  onAdditionalInformationChange(state: boolean): void {
    this.privacySettings.marketingOptIn = state;
  }

  onPersonalizedCoachingChange(state: boolean): void {
    this.privacySettings.allowPersonalizedCoaching = state;
  }

  onHipaaChange(state: boolean): void {
    this.hipaaConsented = state;
  }
}
